import React, { useEffect, useState } from "react";
import { AlertCircle, ClipboardList, Loader2, Mail, Shield } from "lucide-react";
import { getPublicIntake } from "../../utils/api/endpoints/questionnaire";
import { IntakeFormComponent } from "../../components/scoping/IntakeFormComponent";
import { intakeTokens } from "../../components/scoping/intake-ui";

type LockScreenProps = {
  shareToken: string | null;
  status?: string;
  onUnlocked: () => void;
};

const BusinessLockScreen: React.FC<LockScreenProps> = ({ shareToken, status, onUnlocked }) => {
  const [loading, setLoading] = useState(true);
  const [intakeData, setIntakeData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchIntake = async () => {
      if (status === "SUBMITTED") {
        setLoading(false);
        return;
      }
      if (!shareToken) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await getPublicIntake(shareToken);
        setIntakeData(data);
      } catch (err: any) {
        console.error("Error fetching locked scoping intake:", err);
        setError("We couldn't load your scoping form. Please contact your solutions specialist.");
      } finally {
        setLoading(false);
      }
    };
    fetchIntake();
  }, [shareToken, status]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 text-slate-600">
        <Loader2 className="w-9 h-9 text-ctb-blue animate-spin" />
        <p className="text-sm font-medium">Loading your scoping workspace…</p>
      </div>
    );
  }

  return (
    <div className="py-4 px-3 sm:px-4 max-w-6xl mx-auto space-y-4">
      <div className={`${intakeTokens.card} p-5 flex flex-col md:flex-row md:items-center gap-4`}>
        <div className="flex items-start gap-3 flex-1">
          <div className="w-10 h-10 rounded-lg bg-ctb-blue/5 border border-ctb-blue/10 flex items-center justify-center shrink-0">
            <Shield size={20} className="text-ctb-blue" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900">Complete your security scope first</h2>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-xl">
              New partners complete this intake before other dashboard areas unlock. Our team uses
              your answers to size the engagement and prepare your proposal.
            </p>
          </div>
        </div>
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-ctb-blue bg-ctb-blue-0/40 border border-ctb-blue/20 px-3 py-1.5 rounded-md">
          Intake required
        </span>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-start gap-2">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {status === "SUBMITTED" ? (
        <div className={`${intakeTokens.card} p-12 text-center max-w-xl mx-auto space-y-6 shadow-md border border-slate-200/50`}>
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-650 animate-pulse">
            <Shield size={32} />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-bold text-slate-900">Scope under review</h2>
            <p className="text-sm text-slate-500 leading-relaxed max-w-md mx-auto">
              Thank you! Your security scoping intake form has been submitted and is currently being reviewed by our Solutions Specialist.
            </p>
          </div>
          <div className="h-[1px] bg-slate-100 max-w-xs mx-auto" />
          <p className="text-xs text-slate-400">
            No further action is required. We will notify you as soon as the scope has been verified and converted to your active program.
          </p>
        </div>
      ) : shareToken && intakeData ? (
        <IntakeFormComponent
          token={shareToken}
          initialData={intakeData}
          isDark={false}
          onComplete={() => onUnlocked()}
        />
      ) : (
        <div className={`${intakeTokens.card} p-8 text-center max-w-lg mx-auto space-y-4`}>
          <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
            <ClipboardList size={24} />
          </div>
          <h2 className="text-lg font-bold text-slate-900">Scope setup pending</h2>
          <p className="text-sm text-slate-500 leading-relaxed">
            Your intake link hasn&apos;t been created yet. Contact your Solutions Specialist to get
            started.
          </p>
          <a
            href="mailto:support@capturethebug.xyz"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-ctb-blue hover:underline"
          >
            <Mail size={14} />
            support@capturethebug.xyz
          </a>
        </div>
      )}
    </div>
  );
};

export default BusinessLockScreen;
